import { MutationFunctionOptions } from '@apollo/client';
import { RESPONSE_META_KEY } from '@app/client/constants';
import { APP_VERBOSE_ERROR_DISPLAY_SYMBOL } from '@app/GlobalVariables/constants';
import { getGlobalVariable } from '@app/GlobalVariables/util';
import {
  toastOptions as defaultToastOptions,
  toast,
  ToastOptions,
} from '@components/SystemAlerts/Toast';
import { ToasterProps } from '@components/SystemAlerts/Toast/ToastComponent';
import { t } from '@lingui/core/macro';
import { IS_CYPRESS } from '@utils/constants';
import { ExecutionResult, GraphQLError } from 'graphql';
import { flatten, get, isEqual } from 'lodash-es';

export const REQ_ID_KEY = 'x-mastery-req-id' as const;
interface CustomOperationMeta {
  headers: {
    [REQ_ID_KEY]?: string;
  };
}

export const getErrorMessageReqIdPrefix = (
  meta: CustomOperationMeta | undefined,
): string => {
  const reqId = get(meta, ['headers', REQ_ID_KEY]);
  if (reqId) {
    return t`Request ID:` + `\n${reqId}\n`;
  }
  return '';
};

type MutateFn<T> = (
  options?: MutationFunctionOptions,
) => Promise<ExecutionResult<T>>;

interface Options {
  hideSuccessToast?: boolean;
  toastOptions?: Partial<ToastOptions>;
  successMessage?: string;
  showVerboseError?: boolean;
  /** Write a custom formatter that can use any part of the GraphQLErrors array for toast display. If the function returns an empty string or nullish value, a fallback will automatically be displayed. */
  errorFormatter?: (
    error: GraphQLError[],
  ) => Maybe<string | ErrorFormatterResult>;
}

/**
 * Message to be displayed in the toast when an error occurs
 * type can be used to set the type of the toast (e.g., 'error', 'success', etc.)
 */
export interface ErrorFormatterResult {
  message: string;
  type: ToasterProps['type'];
}

const parseErrors = (
  errors: GraphQLError[],
  meta: CustomOperationMeta,
  errorFormatter: Options['errorFormatter'],
): [ErrorFormatterResult, boolean] => {
  const defaultErrorType: ToasterProps['type'] = 'error';

  const firstErr = errors[0];
  try {
    const customFormatted = errorFormatter?.(errors);
    if (customFormatted) {
      if (typeof customFormatted === 'string') {
        return [{ message: customFormatted, type: defaultErrorType }, true];
      } else if (
        typeof customFormatted === 'object' &&
        customFormatted.message
      ) {
        return [
          { message: customFormatted.message, type: customFormatted.type },
          true,
        ];
      }
    }
  } catch {
    //noop
  }

  let prefix = '';
  if (getGlobalVariable(APP_VERBOSE_ERROR_DISPLAY_SYMBOL)) {
    prefix = getErrorMessageReqIdPrefix(meta);
  }
  const path = firstErr?.path || [];

  // We explicitly know about this error, and we want to display it.
  // This one happens when a user tries to pick a carrier code that is already taken
  if (isEqual(path, ['attributes', 'code'])) {
    return [
      {
        message: t`${prefix}Code has already been taken`,
        type: defaultErrorType,
      },
      true,
    ];
  }

  return [
    { message: `${prefix}${firstErr?.message}`, type: defaultErrorType },
    false,
  ];
};

export const getDefaultToastSuccessMsg = (): string => t`Updated`;

export const submitMutation = async <T>(
  mutate: MutateFn<T>,
  options?: Options,
): ReturnType<MutateFn<T>> => {
  try {
    const response = await mutate();
    const { errors: topErrors, data } = response;
    const meta: CustomOperationMeta = get(data, RESPONSE_META_KEY);
    const mutationErrors = flatten(
      Object.values((data as anyOk) || {}).map(
        (obj: anyOk) => obj?.errors || [],
      ),
    );
    // topErrors are things like graphql formatting, or unknown variables + keys
    // mutationErrors are those incurred from the actual mutation sent, which are inside of the mutation name property on data
    const errors = (topErrors || []).concat(mutationErrors);
    const toastOptions: ToastOptions = {
      ...defaultToastOptions,
      ...(options?.toastOptions ?? {}),
    };
    if (errors.length && errors[0]) {
      const [{ message: msgParsed, type: toastType }, allowDisplay] =
        parseErrors(errors, meta, options?.errorFormatter);
      const finalMsg =
        getGlobalVariable(APP_VERBOSE_ERROR_DISPLAY_SYMBOL) ||
        allowDisplay ||
        options?.showVerboseError
          ? msgParsed
          : t`An error occurred when attempting to submit data.`;
      toast({ content: finalMsg, type: toastType, toastOptions });
    } else {
      if (!options?.hideSuccessToast) {
        toast({
          content: options?.successMessage || getDefaultToastSuccessMsg(),
          type: 'success',
          toastOptions,
        });
      }
    }
    return response;
  } catch (err: anyOk) {
    // This catch usually happens when Apollo client deems there to be a NetworkError
    // eslint-disable-next-line no-console
    const str = t`Failed to update. Please try again or contact support.`;
    let formatted: string | undefined;
    let toastType: ToasterProps['type'] = 'error';

    if (options?.errorFormatter && err?.graphQLErrors?.length) {
      const result = options.errorFormatter(err.graphQLErrors);
      if (typeof result === 'string') {
        formatted = result;
      } else if (result && typeof result === 'object' && result.message) {
        formatted = result.message;
        toastType = result.type;
      }
    }
    toast({
      content:
        formatted ||
        (IS_CYPRESS
          ? err?.message
          : (!err?.networkError && err?.message) || str),
      type: toastType,
    });
    return err;
  }
};
